import { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";

import { InfoIcon } from "@/components/wpl/pricing/src/price-finder/price-card/info-icon";
import { wacToken } from "@/libraries/design/foundations";

const TOOLTIP_WIDTH = 250;

type TooltipProps = {
    ariaLabel: string;
    buttonSize?: "small" | "large";
    content: string;
};

export const Tooltip = ({ ariaLabel, buttonSize, content }: TooltipProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipStyle, setTooltipStyle] = useState({});
    const tooltipRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const toggleTooltip = () => {
        setIsVisible((prev) => !prev);
    };

    const calculateTooltipPosition = () => {
        if (tooltipRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const buttonRect =
                tooltipRef.current.parentElement?.getBoundingClientRect();
            const newStyle: React.CSSProperties = {};

            if (buttonRect) {
                // Check if tooltip is going to be cut off on the right side
                const tooltipRight = buttonRect?.left + TOOLTIP_WIDTH;
                const tooltipRightOverflow = tooltipRight - window.innerWidth;
                if (tooltipRightOverflow > TOOLTIP_WIDTH / 2)
                    newStyle.transform = "translateX(-100%)";
                else if (tooltipRightOverflow > 0)
                    newStyle.transform = "translateX(-50%)";

                // Check if tooltip is going to be cut off on the bottom
                const tooltipBottom = buttonRect.bottom + tooltipRect.height;
                if (tooltipBottom > window.innerHeight) {
                    newStyle.bottom = buttonRect.height + "px";
                    newStyle.top = "auto";
                } else {
                    newStyle.bottom = "auto";
                    newStyle.top = "100%";
                }
                setTooltipStyle(newStyle);
            }
        }
    };

    useEffect(() => {
        if (isVisible) {
            calculateTooltipPosition();
        }
    }, [isVisible]);

    // Handle clicks and enter presses outside the tooltip
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                tooltipRef.current &&
                !tooltipRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsVisible(false);
            }
        };

        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
                if (
                    tooltipRef.current &&
                    !tooltipRef.current.contains(document.activeElement) &&
                    buttonRef.current &&
                    !buttonRef.current.contains(document.activeElement)
                ) {
                    setIsVisible(false);
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyPress);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [isVisible]);

    const heightCss = css({ height: `${buttonSize === "small" ? 14 : 22}px` });

    return (
        <div css={[containerCss, heightCss]}>
            <button
                aria-describedby="tooltip"
                aria-label={ariaLabel}
                onClick={toggleTooltip}
                ref={buttonRef}
                css={[buttonCss, heightCss]}
            >
                <InfoIcon size={buttonSize} />
            </button>
            {isVisible && (
                <div
                    id="tooltip"
                    role="tooltip"
                    ref={tooltipRef}
                    css={[toolTipContainerCss, textCss]}
                    className={"body-sm"}
                    style={tooltipStyle}
                >
                    {content}
                </div>
            )}
        </div>
    );
};

const containerCss = css({
    display: "inline-block",
    position: "relative"
});

const buttonCss = css({
    background: "none",
    border: "none",
    padding: "0",
    cursor: "pointer",

    ["&:hover, &:focus-visible"]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "circle:first-of-type": {
            fill: wacToken("color.primary.blue")
        }
    }
});

const toolTipContainerCss = css({
    position: "absolute",
    width: `${TOOLTIP_WIDTH}px`,
    borderRadius: "20px",
    margin: "4px",
    padding: "16px",
    backgroundColor: wacToken("color.extended.blue900"),
    color: wacToken("color.neutral.light0"),
    zIndex: 100
});

const textCss = css({
    whiteSpace: "pre-line",
    textAlign: "left"
});
